import React, { useState, useEffect, useRef } from 'react';
import {
  CustomIconBtn,
  DropdownContainer,
  IconWrapper,
  PopUpContent,
  PopUpFooter,
  PopUpHeader,
  PopUpInputText,
  PopUpSavedFilters,
  PopUpSavedFiltersContainer,
  PopupContainer,
  SavedFilterText,
  SearchBarWrapper,
  StyledSearch
} from './index.element';
import { Checkbox, RadioButton } from 'components';
import Collapse from 'rc-collapse';
import {
  MenuPanel,
  expandIcon
} from 'pages/MaintenanceServiceDashboard/MaintenanceQueryTableByFrequency';
import { tanStackTableMultiFilterOption } from 'reducers/tanStackTable';
import { IconSearch } from 'icons/indexed-icons/IconSearch';
import { StyledTableInput } from 'components/machine-health/configurator/WidgetTable/Form';
import { ChangeEvent } from 'types';
import { IcoFilter } from 'icons/indexed-icons/IcoFilter';
import theme from 'themes';

interface AdvanceFilterPopUpProps {
  options?: tanStackTableMultiFilterOption[];
  handleOptionSelect?: (option: AdvanceFilterOptionType[]) => void;
  searchable?: boolean;
  searchInputValue?: (input?: string) => void;
  selectedOpt?: string | JSX.Element;
  label?: JSX.Element;
  iconColor?: string;
  showSavedFiltersOption?: boolean;
}

export interface AdvanceFilterOptionType {
  name: string;
  value?: string | number;
  label?: string;
}

const AdvanceFilterPopUp: React.FC<AdvanceFilterPopUpProps> = ({
  options,
  handleOptionSelect,
  showSavedFiltersOption
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [searchInput, setSearchInput] = useState({ category: '', searchString: '' });
  const [filterOption, setFilterOption] = useState(options);
  const dropdownRef = useRef<HTMLDivElement>(null); // Ref for the dropdown container

  // Function to toggle the dropdown visibility
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSearchOption = (e: ChangeEvent, category: string) => {
    setSearchInput({ category: category, searchString: e.target.value });

    const filteredList = options
      ?.find((item) => item?.category === category)
      ?.filters?.filter(
        (option) =>
          option?.label?.toLowerCase()?.includes(e.target.value?.toLowerCase()) ||
          option?.value?.toString().toLowerCase()?.includes(e.target.value?.toLowerCase())
      );

    setFilterOption((prevOptions) => {
      return prevOptions.map(option => {
        if (option.category === category) {
          return {
            ...option,
            category,
            filters: filteredList || []
          };
        }
        return option;
      });
    });

  };

  // Effect to handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Add event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [activePanelKeys, setActivePanelKeys] = useState<React.Key[]>([]);

  const [selectedValues, setSelectedValues] = useState([]);

  useEffect(() => {
    handleOptionSelect && handleOptionSelect(selectedValues);
  }, [selectedValues]);

  // Handle Radio button/Checkbox changes
  const handleInputChange = (event, data, singleSelect) => {
    const { checked } = event.target;
    const { name: category, value } = data;
    setSelectedValues((prevSelectedValues) => {
      if (checked) {
        if (singleSelect) {
          // If it's a radio button (singleSelect), clear previous selections within the same category
          return [
            ...prevSelectedValues.filter((item) => item.name !== category),
            data
          ];
        } else {
          // Add value to the list if checked and it's a checkbox
          return [...prevSelectedValues, data];
        }
      } else {
        // Remove value from the list if unchecked
        return prevSelectedValues.filter((item) => item?.value !== value);
      }
    });
  };

  return (
    <>
      <DropdownContainer ref={dropdownRef}>
        <CustomIconBtn isActive={isOpen} onClick={toggleDropdown}>
          <IcoFilter width="16" height="16" />
        </CustomIconBtn>
        {isOpen && (
          <PopupContainer top="2rem">
            { showSavedFiltersOption && (<PopUpSavedFiltersContainer>
              <SavedFilterText>Saved Filters</SavedFilterText>
              <PopUpSavedFilters>no filters</PopUpSavedFilters>
            </PopUpSavedFiltersContainer>)
            }
            <Collapse
              destroyInactivePanel={true}
              activeKey={activePanelKeys}
              expandIcon={({ ...props }) =>
                expandIcon({ color: theme.colors.primaryBlue5, ...props })
              }
              onChange={(key: React.Key | React.Key[]) =>
                setActivePanelKeys(Array.isArray(key) ? key : [key])
              }
            >
              {filterOption &&
                filterOption.map((panel) => {
                  const { category, searchable, filters, singleSelect } = panel;
                  const InputComponent = singleSelect ? RadioButton : Checkbox;
                  const inputType = singleSelect ? 'radio' : 'checkbox';
                  return (
                    <MenuPanel
                      showArrow={true}
                      collapsible="header"
                      key={panel?.category}
                      header={<PopUpHeader>{panel?.category}</PopUpHeader>}
                      mb={0}
                    >
                      {searchable && (
                        <StyledSearch>
                          <SearchBarWrapper>
                            <IconWrapper>
                              <IconSearch width="15" />
                            </IconWrapper>
                            <StyledTableInput
                              type="text"
                              value={searchInput?.searchString}
                              onChange={(e) => {
                                handleSearchOption(e, panel?.category);
                              }}
                              placeholder={`Search ${panel?.category}`}
                              style={{
                                paddingLeft: '2rem',
                                width: '92%'
                              }}
                              borderVariant={'none'}
                              bgColor={theme.colors.white}
                            />
                          </SearchBarWrapper>
                        </StyledSearch>
                      )}
                      {filters.map((item) => {
                        const { value, label } = item;
                        const isChecked = !!selectedValues?.find((selectedValue) => selectedValue.value === value);
                        return (
                          <PopUpContent htmlFor={`${inputType}-${value}`} key={value}>
                            <InputComponent
                              width={20}
                              height={20}
                              label=""
                              checked={isChecked}
                              onChange={(e) =>
                                handleInputChange(e, {
                                  name: category,
                                  value: value
                                }, singleSelect)
                              }
                              id={value?.toString()}
                              name={singleSelect ? category : undefined}
                            />
                            <PopUpInputText>{label}</PopUpInputText>
                          </PopUpContent>
                        );
                      })}
                    </MenuPanel>
                  );
                })}
            </Collapse>
            <PopUpFooter>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <p className={`clear-btn${selectedValues.length === 0 ? ' disabled' : ''}`} onClick={() => setSelectedValues([])}>
                  Clear
                </p>
                <p className='apply-btn' onClick={() => setIsOpen(!isOpen)}>
                  Apply
                </p>
              </div>
            </PopUpFooter>
          </PopupContainer>
        )}
      </DropdownContainer>
    </>
  );
};

export default AdvanceFilterPopUp;
